import React, { Fragment, useState, useRef, useEffect } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { NavLink } from "react-router-dom";
import Modal from "react-modal";
import {
  Bars3Icon,
  BellIcon,
  XMarkIcon,
  UserCircleIcon,
  CheckIcon,
  EyeIcon,
  UsersIcon,
  BookOpenIcon,
  LockClosedIcon,
  UserPlusIcon,
  HomeIcon,
  CreditCardIcon,
  WalletIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import useAuth from "../hooks/useAuth";
import Greeting from "./Greeting";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import "./ModalStyles.css";
import { BsFillLockFill } from "react-icons/bs";
import { FaPlusSquare, FaRocket } from "react-icons/fa";
import axios from "axios";

import "datatables.net-dt/css/jquery.dataTables.css";
import $ from "jquery";
import "datatables.net";
import MyComponent from "./MyComponent";

// Utility function to combine class names
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Set the app element for react-modal
Modal.setAppElement("#root");

export default function Users() {
  /***********************
   * AUTH & ROLE SETUP
   ***********************/
  const { auth, setAuth } = useAuth();
  const roles = auth?.roles || [];
  const entID = auth?.result?.entID;
  // Define role booleans for easy checks
  const hasAdminRole = roles.includes(5150);
  const hasEntRole = roles.includes(1984);
  const hasUserRole = roles.includes(2001);
  const hasAuditorRole = roles.includes(2023);
  const hasUnitRole = roles.includes(1981);
  const authEmail = auth?.user;

  /***********************
   * SIDEBAR & LOGOUT TIMER
   ***********************/
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const logoutTimer = useRef(null);

  /***********************
   * AUDIT TRAIL DATA (IP, country, userAgent)
   ***********************/
  const [userIP, setUserIP] = useState("");
  const [country, setCountry] = useState("");
  const [userAgent, setUserAgent] = useState("");

  // Fetch the client IP and country details on component mount
  useEffect(() => {
    const fetchUserDataIP = async () => {
      try {
        const response = await axios.get("https://ipapi.co/json/");
        const data = response.data;
        setUserIP(data.ip);
        setCountry(data.country_name);
      } catch (error) {
        console.log("Error fetching user data:", error);
      }
    };
    fetchUserDataIP();
    setUserAgent(navigator.userAgent);
  }, []);

  const axiosPrivate = useAxiosPrivate();

  // Logout function that also sends an audit trail entry
  const handleLogout = async () => {
    let userType = "";
    if (hasAdminRole) {
      userType = "Administrator";
    } else if (hasEntRole) {
      userType = "Enterprise";
    } else if (hasUserRole) {
      userType = "User";
    } else if (hasAuditorRole) {
      userType = "Auditor";
    } else if (hasUnitRole) {
      userType = "Unit Processor";
    }

    const auditTrailData = {
      userId: auth?.user,
      action: "Logout",
      details: {
        ipAddress: userIP,
        userAgent: userAgent,
        country: country,
      },
      status: "Successful",
      usertype: userType,
    };

    try {
      await axiosPrivate.post("/auditTrail", auditTrailData);
    } catch (error) {
      console.error("Error saving audit trail entry:", error);
    }
    setAuth(null);
    localStorage.removeItem("auth");
  };

  // Set a logout timer (15 minutes) and reset on user activity
  useEffect(() => {
    const resetLogoutTimer = () => {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = setTimeout(() => {
        handleLogout();
      }, 15 * 60 * 1000);
    };

    resetLogoutTimer();
    document.addEventListener("mousemove", resetLogoutTimer);
    document.addEventListener("keydown", resetLogoutTimer);

    return () => {
      clearTimeout(logoutTimer.current);
      document.removeEventListener("mousemove", resetLogoutTimer);
      document.removeEventListener("keydown", resetLogoutTimer);
    };
  }, [userIP, userAgent, country]);

  /***********************
   * NAVIGATION CONFIGURATION
   ***********************/
  const rawNavigation = [
    { name: "Home", to: "/", icon: HomeIcon, current: false },
    { name: "Verify", to: "/verify", icon: CheckIcon, current: false },
    { name: "View Verification", to: "/view", icon: EyeIcon, current: false },
    { name: "Units", to: "/units", icon: CreditCardIcon, current: false },
    { name: "Payments", to: "/payment", icon: WalletIcon, current: false },
    { name: "View Users", to: "/users", icon: UsersIcon, current: true },
    {
      name: "Create User",
      to: "/createuser",
      icon: UserPlusIcon,
      current: false,
    },
    {
      name: "Create Price",
      to: "/createprice",
      icon: BanknotesIcon,
      current: false,
    },
    {
      name: "View Price",
      to: "/price",
      icon: CurrencyDollarIcon,
      current: false,
    },
    {
      name: "API Documentation",
      to: "/documentation",
      icon: BookOpenIcon,
      current: false,
    },
    {
      name: "Change Password",
      to: "/password",
      icon: LockClosedIcon,
      current: false,
    },
    { name: "View Logs", to: "/audit", icon: EyeIcon, current: false },
  ];

  // Filter navigation items based on the current user's roles and enterprise ID
  const navigation = rawNavigation.filter((item) => {
    if (item.name === "Units" || item.name === "Payments") {
      return (
        (auth?.result?.entID === 1 && hasUserRole) ||
        (auth?.result?.entID !== 1 && hasEntRole) ||
        (auth?.result?.entID === 1 && hasAdminRole)
      );
    } else if (item.name === "View Users") {
      // Allow View Users for admin, enterprise, or unit processor
      return hasAdminRole || hasEntRole || hasUnitRole;
    } else if (
      item.name === "Create User" ||
      item.name === "API Documentation"
    ) {
      return hasAdminRole || hasEntRole;
    } else if (item.name === "Create Price" || item.name === "View Price") {
      return hasAdminRole;
    } else if (item.name === "View Logs") {
      return hasAdminRole || hasAuditorRole;
    } else {
      return true;
    }
  });

  /***********************
   * MODAL: CHANGE PASSWORD
   ***********************/
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");

  const openPwdModal = (email) => {
    setPasswordData((prev) => ({ ...prev, email }));
    setPwdModalOpen(true);
  };

  const closePwdModal = () => {
    setPasswordData({ email: "", password: "", confirmPassword: "" });
    setPasswordError("");
    setPwdModalOpen(false);
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  /***********************
   * MODAL: ADD UNIT
   ***********************/
  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [unitData, setUnitData] = useState({ email: "", amount: "" });
  const [unitError, setUnitError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Options for unit selection
  const unitOptions = [
    { label: "Choose one", value: "" },
    { label: "1", value: "1" },
    { label: "2", value: "2" },
    { label: "3", value: "3" },
    { label: "4", value: "4" },
    { label: "5", value: "5" },
    { label: "6", value: "6" },
    { label: "7", value: "7" },
    { label: "8", value: "8" },
    { label: "9", value: "9" },
    { label: "10", value: "10" },
    { label: "20", value: "20" },
    { label: "30", value: "30" },
    { label: "40", value: "40" },
    { label: "50", value: "50" },
    { label: "60", value: "60" },
    { label: "70", value: "70" },
    { label: "80", value: "80" },
    { label: "90", value: "90" },
    { label: "100", value: "100" },
    { label: "500", value: "500" },
    { label: "1000", value: "1000" },
  ];

  const openUnitModal = (email) => {
    setUnitData((prev) => ({ ...prev, email }));
    setUnitModalOpen(true);
  };

  const closeUnitModal = () => {
    setUnitData({ email: "", amount: "" });
    setUnitError("");
    setUnitModalOpen(false);
  };

  const handleUnitChange = (e) => {
    const { name, value } = e.target;
    setUnitData((prev) => ({ ...prev, [name]: value }));
  };

  // Helper function to determine the user type from roles
  const getUserType = () => {
    if (hasAdminRole) return "Administrator";
    if (hasEntRole) return "Enterprise";
    if (hasUserRole) return "User";
    if (hasAuditorRole) return "Auditor";
    if (hasUnitRole) return "Unit Processor";
    return "Unknown";
  };

  const handleUnitSubmit = async (e) => {
    e.preventDefault();
    setUnitError("");
    // Convert the amount to a number
    const dataToSend = { ...unitData, amount: Number(unitData.amount) };

    try {
      setIsLoading(true);
      if (hasAdminRole || hasUnitRole) {
        await axiosPrivate.post("/newunit", dataToSend);
      } else if (hasEntRole) {
        const newData = { email: authEmail, user: dataToSend };
        if (newData.email === dataToSend.email) {
          alert(
            "Your unit cannot be updated by yourself. Buy unit or contact administrator to add unit."
          );
          return;
        }
        await axiosPrivate.post("/updateunit", newData);
      }

      // Build an action string like "2 unit added to user@example.com"
      const unitText = dataToSend.amount === 1 ? "unit" : "units"; // handle singular vs plural
      const actionMessage = `${dataToSend.amount} ${unitText} added to ${dataToSend.email}`;

      // Record an audit trail entry for the unit add action
      const auditTrailData = {
        userId: auth?.user,
        action: actionMessage,
        details: {
          ipAddress: userIP,
          userAgent: userAgent,
          country: country,
        },
        status: "Successful",
        usertype: getUserType(),
      };
      await axiosPrivate.post("/auditTrail", auditTrailData);

      alert(`${dataToSend.amount} ${unitText} added`);
      closeUnitModal();
      await fetchUserData();
    } catch (error) {
      if (
        error.response &&
        error.response.status >= 400 &&
        error.response.status <= 500
      ) {
        setUnitError(error.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError("");
    const { email, password, confirmPassword } = passwordData;

    if (password !== confirmPassword) {
      setPasswordError("Password and confirm password do not match!");
      return;
    }

    // Validate password using regex
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}$/;
    if (!regex.test(password)) {
      setPasswordError(
        "Invalid password. It should contain at least one lowercase letter, one uppercase letter, one numeric digit, one special character and be at least eight characters long."
      );
      return;
    }

    try {
      // Update the password
      await axiosPrivate.post("/changePassword", { email, pwd: password });

      // Build the audit action message
      const actionMessage = `Password changed for ${email}`;

      // Record an audit trail entry
      const auditTrailData = {
        userId: auth?.user,
        action: actionMessage,
        details: {
          ipAddress: userIP,
          userAgent: userAgent,
          country: country,
        },
        status: "Successful",
        usertype: getUserType(),
      };
      await axiosPrivate.post("/auditTrail", auditTrailData);

      alert("User password changed successfully");
      closePwdModal();
    } catch (error) {
      if (error.response) {
        setPasswordError(error.response.data);
      } else if (error.request) {
        setPasswordError(error.request);
      } else {
        setPasswordError(error.message);
      }
    }
  };

  /***********************
   * MODAL: UPGRADE TO ENTERPRISE
   ***********************/
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeUserEmail, setUpgradeUserEmail] = useState("");
  const [enterpriseName, setEnterpriseName] = useState("");
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [upgradeError, setUpgradeError] = useState("");
  const [isUpgradeSubmitting, setIsUpgradeSubmitting] = useState(false);

  const openUpgradeModal = (email) => {
    setUpgradeUserEmail(email);
    setEnterpriseName("");
    setUpgradeMessage("");
    setUpgradeError("");
    setUpgradeModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setUpgradeModalOpen(false);
  };

  const handleUpgrade = async () => {
    // Generate a unique three-digit number between 100 and 999
    const generatedEntID = Math.floor(Math.random() * 900) + 100;
    const payload = {
      email: upgradeUserEmail,
      entID: generatedEntID,
      entName: enterpriseName,
    };

    try {
      setIsUpgradeSubmitting(true);
      const response = await axiosPrivate.post("/upgradeUser", payload);
      if (
        response.status === 200 &&
        response.data.message === "User upgraded to enterprise successfully."
      ) {
        setUpgradeMessage("User upgraded to enterprise successfully.");
        await fetchUserData();
        setTimeout(() => {
          closeUpgradeModal();
        }, 1500);
      } else {
        setUpgradeError("Upgrade failed. Please try again.");
      }
    } catch (error) {
      console.error("Error upgrading user:", error);
      setUpgradeError("Error upgrading user: " + error.message);
    } finally {
      setIsUpgradeSubmitting(false);
    }
  };

  /***********************
   * FETCH USERS & TABLE DATA
   ***********************/
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState([]);

  const fetchUserData = async () => {
    try {
      if (hasAdminRole || hasUnitRole) {
        const response = await axiosPrivate.get("/fetchuser");
        setUserData(response.data);
      } else if (hasEntRole) {
        const response = await axiosPrivate.post("/fetchuser", { entID });
        setUserData(response.data.mergedData);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  // Initialize jQuery DataTable after data is loaded
  useEffect(() => {
    if (!loading) {
      $("#userDataTable").DataTable();
    }
  }, [loading]);

  /***********************
   * ENABLE/DISABLE USER STATUS
   ***********************/
  const handleUserStatusChange = async (email, user) => {
    try {
      if (authEmail === email) {
        alert("You cannot disable or enable your own account");
        return;
      }
      await axiosPrivate.put("/updateenable", {
        email,
        enabled: !user.enabled,
      });
      fetchUserData();
    } catch (error) {
      console.error("Failed to update user status:", error.message);
    }
  };

  /***********************
   * DELETE USER FUNCTION
   ***********************/
  const handleDeleteUser = async (user) => {
    if (authEmail === user.email) {
      alert("You cannot delete your own account");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the user ${user.email}?`
    );

    if (confirmDelete) {
      let role = "";
      if (user.roles.Admin) role = "Administrator";
      if (user.roles.User) role = "User";
      if (user.roles.Auditor) role = "Auditor";
      if (user.roles.Ent) role = "Enterprise";

      const payload = {
        email: user.email,
        entID: user.entID,
        roles: role,
      };

      try {
        const response = await axiosPrivate.post("/deleteUser", payload);
        if (response.status === 200) {
          setUserData((prevData) =>
            prevData.filter((item) => item.email !== user.email)
          );
          alert("User deleted successfully!");
        } else {
          alert(
            `Failed to delete user. Error: ${
              response.data?.message || "Unknown error"
            }`
          );
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        alert(
          `An error occurred: ${
            error.response?.data?.message || "Please try again."
          }`
        );
      }
    }
  };

  /***********************
   * RENDER COMPONENT
   ***********************/
  return (
    <>
      <div>
        {/* Mobile Sidebar */}
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button
                        type="button"
                        className="-m-2.5 p-2.5"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-6 w-6 text-white"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                    <div className="flex h-16 shrink-0 items-center mt-4">
                      <img className="logo w-auto" src="logo.png" alt="Logo" />
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            {navigation.map((item) => (
                              <li key={item.name}>
                                <NavLink
                                  to={item.to}
                                  className={classNames(
                                    item.current
                                      ? "bg-gray-50 text-blue-600"
                                      : "text-gray-700 hover:text-blue-600 hover:bg-gray-50",
                                    "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold"
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      item.current
                                        ? "text-blue-600"
                                        : "text-gray-400 group-hover:text-blue-600",
                                      "h-6 w-6 shrink-0"
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Desktop Sidebar */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center mt-4">
              <img className="logo w-auto" src="logo.png" alt="Logo" />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <NavLink
                          to={item.to}
                          className={classNames(
                            item.current
                              ? "bg-gray-50 text-blue-600"
                              : "text-gray-700 hover:text-blue-600 hover:bg-gray-50",
                            "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold"
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-blue-600"
                                : "text-gray-400 group-hover:text-blue-600",
                              "h-6 w-6 shrink-0"
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:pl-72">
          <div className="sticky top-0 z-40 lg:mx-auto lg:max-w-7xl lg:px-8">
            <div className="flex h-16 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-0 lg:shadow-none">
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>
              <div
                className="h-6 w-px bg-gray-200 lg:hidden"
                aria-hidden="true"
              />
              <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6 border-b-2 border-blue-400">
                <form className="relative flex flex-1" action="#" method="GET">
                  <label htmlFor="search-field" className="sr-only">
                    Search
                  </label>
                </form>
                <div className="flex items-center gap-x-4 lg:gap-x-6">
                  <button
                    type="button"
                    className="-m-2.5 p-2.5 text-blue-400 hover:text-blue-500"
                  >
                    <span className="sr-only">View notifications</span>
                    <BellIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                  <div
                    className="hidden lg:block lg:h-6 lg:w-px lg:bg-blue-200"
                    aria-hidden="true"
                  />
                  <Menu as="div" className="relative">
                    <Menu.Button className="-m-1.5 flex items-center p-1.5">
                      <span className="sr-only">Open user menu</span>
                      <UserCircleIcon
                        className="h-8 w-8 rounded-full text-blue-400"
                        aria-hidden="true"
                      />
                      <span className="hidden lg:flex lg:items-center">
                        <span
                          className="ml-4 text-sm font-semibold leading-6 text-gray-900"
                          aria-hidden="true"
                        >
                          {auth?.result?.firstName} {auth?.result?.lastName}
                        </span>
                        <ChevronDownIcon
                          className="ml-2 h-5 w-5 text-blue-400"
                          aria-hidden="true"
                        />
                      </span>
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                        <Menu.Item>
                          {({ active }) => (
                            <NavLink
                              to="/password"
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              Your Password
                            </NavLink>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              onClick={handleLogout}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              Logout
                            </a>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>
          </div>

          <main className="py-10">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="bg-white shadow border-b-2 border-blue-400">
                <div className="px-4 sm:px-6 lg:mx-auto lg:max-w-6xl lg:px-8">
                  <div className="py-6 md:flex md:items-center md:justify-between lg:border-t lg:border-blue-400">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center">
                        <UserCircleIcon
                          className="hidden h-16 w-16 rounded-full sm:block"
                          aria-hidden="true"
                        />
                        <div>
                          <div className="flex items-center">
                            <UserCircleIcon
                              className="hidden h-16 w-16 rounded-full sm:hidden"
                              aria-hidden="true"
                            />
                            <h1 className="ml-3 text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:leading-9">
                              <Greeting />, {auth?.result?.firstName}{" "}
                              {auth?.result?.lastName}
                            </h1>
                          </div>
                          <dl className="mt-6 flex flex-col sm:ml-3 sm:mt-1 sm:flex-row sm:flex-wrap">
                            <dt className="sr-only">Account status</dt>
                            <dd className="mt-3 flex items-center text-sm font-medium capitalize text-blue-500 sm:mr-6 sm:mt-0">
                              <CheckCircleIcon
                                className="mr-1.5 h-5 w-5 flex-shrink-0 text-blue-400"
                                aria-hidden="true"
                              />
                              Verified account
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex space-x-3 md:ml-4 md:mt-0">
                      <MyComponent auth={auth} roles={roles} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                  <h2 className="text-3xl font-bold leading-8 text-gray-900 pb-5">
                    User Information
                  </h2>
                  <div className="overflow-x-auto">
                    <div>
                      {loading ? (
                        <p>Loading...</p>
                      ) : (
                        <table id="userDataTable">
                          <thead>
                            <tr>
                              <th>First Name</th>
                              <th>Last Name</th>
                              <th>Email</th>
                              <th>Role</th>
                              <th>AU/UU</th>
                              <th>Action</th>
                              <th>Enterprise Name</th>
                              <th>Enterprise ID</th>
                              <th>Email Verification</th>
                              <th>User Status</th>
                              {hasAdminRole && <th>Delete</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {userData.map((user) => (
                              <tr key={user.email}>
                                <td>{user.firstName}</td>
                                <td>{user.lastName}</td>
                                <td>{user.email}</td>
                                <td>
                                  {user.roles.Admin && "Administrator"}
                                  {user.roles.User && "User"}
                                  {user.roles.Auditor && "Auditor"}
                                  {user.roles.Ent && "Enterprise"}
                                  {user.roles.Unit && "Unit Processor"}
                                </td>
                                <td>{`${user.au}/${user.uu}`}</td>
                                <td>
                                  <div style={{ display: "flex", gap: "5px" }}>
                                    <BsFillLockFill
                                      onClick={() => openPwdModal(user.email)}
                                      style={{ cursor: "pointer" }}
                                    />
                                    <FaPlusSquare
                                      onClick={() => openUnitModal(user.email)}
                                      style={{ cursor: "pointer" }}
                                    />
                                    {hasAdminRole &&
                                      user.roles &&
                                      user.roles.User &&
                                      (!user.entName ||
                                        user.entName.trim() === "") && (
                                        <FaRocket
                                          onClick={() =>
                                            openUpgradeModal(user.email)
                                          }
                                          style={{ cursor: "pointer" }}
                                        />
                                      )}
                                  </div>
                                </td>
                                <td>{user.entName}</td>
                                <td>{user.entID}</td>
                                <td
                                  style={{
                                    color: user.verified ? "green" : "red",
                                  }}
                                >
                                  {user.verified
                                    ? "User email verified"
                                    : "User email not verified"}
                                </td>
                                <td>
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleUserStatusChange(user.email, user);
                                    }}
                                    style={{
                                      color: user.enabled ? "green" : "red",
                                    }}
                                  >
                                    {user.enabled
                                      ? "Disable User"
                                      : "Enable User"}
                                  </a>
                                </td>
                                {hasAdminRole && (
                                  <td>
                                    <button
                                      onClick={() => handleDeleteUser(user)}
                                      style={{
                                        backgroundColor: "red",
                                        color: "white",
                                        border: "none",
                                        padding: "5px 10px",
                                        borderRadius: "5px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Password Modal */}
              <Modal
                isOpen={pwdModalOpen}
                onRequestClose={closePwdModal}
                className="ModalContainer"
                overlayClassName="ModalOverlay"
              >
                <div className="PasswordModalContent">
                  <h2 className="ModalTitle">Change Password</h2>
                  <form onSubmit={handlePasswordChange}>
                    <input
                      type="password"
                      name="password"
                      placeholder="New password"
                      value={passwordData.password}
                      onChange={handlePasswordInputChange}
                      className="ModalInput"
                    />
                    <input
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm password"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordInputChange}
                      className="ModalInput"
                    />
                    {passwordError && (
                      <p className="ModalText Error">{passwordError}</p>
                    )}
                    <div className="ModalButtonContainer">
                      <button
                        type="submit"
                        className="ModalButton Primary"
                        disabled={isLoading}
                      >
                        {isLoading ? "Changing..." : "Change Password"}
                      </button>
                      <button
                        className="ModalButton Secondary"
                        onClick={closePwdModal}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </Modal>

              {/* Unit Modal */}
              <Modal
                isOpen={unitModalOpen}
                onRequestClose={closeUnitModal}
                className="ModalContainer"
                overlayClassName="ModalOverlay"
              >
                <div className="UnitModalContent">
                  <h2 className="ModalTitle">Add Unit</h2>
                  <form onSubmit={handleUnitSubmit}>
                    <select
                      name="amount"
                      value={unitData.amount}
                      onChange={handleUnitChange}
                      className="ModalInput"
                    >
                      {unitOptions.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {unitError && (
                      <p className="ModalText Error">{unitError}</p>
                    )}
                    <div className="ModalButtonContainer">
                      <button
                        type="submit"
                        className="ModalButton Primary"
                        disabled={isLoading}
                      >
                        {isLoading ? "Adding..." : "Add Unit"}
                      </button>
                      <button
                        className="ModalButton Secondary"
                        onClick={closeUnitModal}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </Modal>

              {/* Upgrade Modal */}
              <Modal
                isOpen={upgradeModalOpen}
                onRequestClose={closeUpgradeModal}
                className="ModalContainer"
                overlayClassName="ModalOverlay"
              >
                <div
                  className="UpgradeModalContent"
                  style={{
                    backgroundColor: "white",
                    padding: "30px",
                    maxWidth: "600px",
                    width: "90%",
                    margin: "40px auto",
                    borderRadius: "8px",
                    boxShadow: "0 5px 15px rgba(0, 0, 0, 0.3)",
                    textAlign: "center",
                  }}
                >
                  <h2 className="ModalTitle">Upgrade to Enterprise</h2>
                  <button
                    onClick={closeUpgradeModal}
                    className="ModalCloseButton"
                    style={{ float: "right" }}
                  >
                    Close
                  </button>
                  <div style={{ marginBottom: "20px" }}>
                    <p>
                      <strong>User Email:</strong> {upgradeUserEmail}
                    </p>
                    <label htmlFor="enterpriseName">Enterprise Name:</label>
                    <input
                      type="text"
                      id="enterpriseName"
                      value={enterpriseName}
                      onChange={(e) => setEnterpriseName(e.target.value)}
                      className="ModalInput"
                      placeholder="Enter Enterprise Name"
                      style={{
                        width: "100%",
                        padding: "8px",
                        marginTop: "10px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                  <div
                    className="ModalButtonContainer"
                    style={{ marginTop: "20px" }}
                  >
                    <button
                      onClick={handleUpgrade}
                      className="ModalButton Primary"
                      disabled={isUpgradeSubmitting}
                      style={{
                        padding: "10px 20px",
                        fontSize: "1rem",
                        borderRadius: "4px",
                      }}
                    >
                      {isUpgradeSubmitting
                        ? "Upgrading..."
                        : "UPGRADE TO ENTERPRISE"}
                    </button>
                  </div>
                  {upgradeMessage && (
                    <p className="ModalText Success">{upgradeMessage}</p>
                  )}
                  {upgradeError && (
                    <p className="ModalText Error">{upgradeError}</p>
                  )}
                </div>
              </Modal>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
